import React, { useState, useMemo } from 'react';
import { Search, Filter, Grid, List, Sparkles, TrendingUp, Award } from 'lucide-react';
import TemplateCard from './TemplateCard';

/**
 * Enhanced Template Selector with filtering, search, and comparison
 */
const TemplateSelector = ({ templates, selectedTemplate, onSelect }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStyle, setSelectedStyle] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [sortBy, setSortBy] = useState('popular'); // 'popular', 'name', 'sections'

    // Style categories for filtering
    const styleCategories = [
        { value: 'all', label: 'All Styles', count: templates.length },
        { value: 'minimalist', label: 'Minimalist', keywords: ['minimalist', 'clean', 'simple'] },
        { value: 'luxury', label: 'Luxury', keywords: ['prestige', 'luxury', 'premium', 'elegant'] },
        { value: 'modern', label: 'Modern', keywords: ['modern', 'contemporary', 'dynamic'] },
        { value: 'creative', label: 'Creative', keywords: ['artistic', 'creative', 'studio', 'craft'] },
        { value: 'bold', label: 'Bold', keywords: ['impact', 'bold', 'vintage', 'vogue'] }
    ];

    // Popular templates (customize based on your analytics)
    const popularTemplateIds = [10, 11, 14, 15, 17];

    // Filter and sort templates
    const filteredTemplates = useMemo(() => {
        let filtered = templates.filter(template => {
            // Search filter
            const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                template.features.some(f => f.toLowerCase().includes(searchQuery.toLowerCase()));

            if (!matchesSearch) return false;

            // Style filter
            if (selectedStyle === 'all') return true;

            const styleCategory = styleCategories.find(s => s.value === selectedStyle);
            if (!styleCategory?.keywords) return true;

            return styleCategory.keywords.some(keyword =>
                template.name.toLowerCase().includes(keyword) ||
                template.description.toLowerCase().includes(keyword)
            );
        });

        // Sort templates
        switch (sortBy) {
            case 'popular':
                filtered.sort((a, b) => {
                    const aPopular = popularTemplateIds.includes(a.id);
                    const bPopular = popularTemplateIds.includes(b.id);
                    if (aPopular && !bPopular) return -1;
                    if (!aPopular && bPopular) return 1;
                    return a.id - b.id;
                });
                break;
            case 'name':
                filtered.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'sections':
                filtered.sort((a, b) => b.sections.length - a.sections.length);
                break;
            default:
                break;
        }

        return filtered;
    }, [templates, searchQuery, selectedStyle, sortBy]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <div className="max-w-full mx-auto px-6 lg:px-12 py-12 space-y-10">
                {/* Hero Header */}
                <div className="text-center space-y-6 pb-12">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 rounded-full text-lg font-bold mb-6 shadow-sm border border-amber-200">
                        <Sparkles className="w-6 h-6" />
                        Choose Your Perfect Template
                    </div>
                    <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 leading-tight">
                        Select a Template to<br />
                        <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            Get Started
                        </span>
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                        Choose from <span className="font-bold text-amber-700">{templates.length}</span> professionally designed templates.
                        Each template is fully customizable to match your unique brand identity.
                    </p>
                </div>

                {/* Search and Filter Bar */}
                <div className="bg-white rounded-3xl shadow-2xl border border-amber-100 p-8 lg:p-10 space-y-8">
                    {/* Search Bar */}
                    <div className="relative">
                        <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-amber-400 w-6 h-6" />
                        <input
                            type="text"
                            placeholder="Search templates by name, style, or features..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-16 pr-6 py-6 border-2 border-amber-200 rounded-2xl focus:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-100 transition-all text-lg text-gray-900 placeholder-gray-500 shadow-sm"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    )}
                </div>

                    {/* Filter Controls */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                        {/* Style Filter */}
                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                            <div className="flex items-center gap-3">
                                <Filter className="w-5 h-5 text-amber-600" />
                                <span className="text-lg font-bold text-gray-800">Filter by Style:</span>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {styleCategories.map(style => (
                                    <button
                                        key={style.value}
                                        onClick={() => setSelectedStyle(style.value)}
                                        className={`px-6 py-3 rounded-xl text-base font-semibold transition-all duration-300 ${
                                            selectedStyle === style.value
                                                ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg transform scale-105'
                                                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 hover:border-amber-300'
                                        }`}
                                    >
                                        {style.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="lg:ml-auto flex flex-col lg:flex-row lg:items-center gap-4">
                            {/* Sort Dropdown */}
                            <div className="flex items-center gap-3">
                                <span className="text-lg font-bold text-gray-800">Sort:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="px-6 py-3 border-2 border-amber-200 rounded-xl text-base font-semibold text-amber-800 focus:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-100 bg-amber-50 shadow-sm"
                                >
                                    <option value="popular">Most Popular</option>
                                    <option value="name">Name (A-Z)</option>
                                    <option value="sections">Most Features</option>
                                </select>
                            </div>

                            {/* View Mode Toggle */}
                            <div className="flex gap-2 bg-amber-50 p-2 rounded-xl border border-amber-200">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-3 rounded-lg transition-all duration-300 ${
                                        viewMode === 'grid'
                                            ? 'bg-white text-amber-600 shadow-md border border-amber-300'
                                            : 'text-amber-400 hover:text-amber-600'
                                    }`}
                                >
                                    <Grid className="w-6 h-6" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-3 rounded-lg transition-all duration-300 ${
                                        viewMode === 'list'
                                            ? 'bg-white text-amber-600 shadow-md border border-amber-300'
                                            : 'text-amber-400 hover:text-amber-600'
                                    }`}
                                >
                                    <List className="w-6 h-6" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results Counter */}
                <div className="flex items-center justify-between text-lg text-gray-700 bg-white rounded-2xl p-6 shadow-lg border border-amber-100">
                    <p className="font-semibold">
                        Showing <span className="font-bold text-amber-700">{filteredTemplates.length}</span> template{filteredTemplates.length !== 1 ? 's' : ''}
                        {searchQuery && (
                            <span> for "<span className="font-bold text-orange-600">{searchQuery}</span>"</span>
                        )}
                    </p>
                    {filteredTemplates.length === 0 && (
                        <button
                            onClick={() => {
                                setSearchQuery('');
                                setSelectedStyle('all');
                            }}
                            className="text-amber-600 hover:text-amber-700 font-bold bg-amber-50 px-6 py-2 rounded-xl border border-amber-200 hover:border-amber-300 transition-all"
                        >
                            Clear filters
                        </button>
                    )}
                </div>

                {/* Templates Grid/List */}
                {filteredTemplates.length > 0 ? (
                    <div className={viewMode === 'grid'
                        ? 'grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-8 lg:gap-12'
                        : 'space-y-8'
                    }>
                        {filteredTemplates.map(template => (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                isSelected={selectedTemplate?.id === template.id}
                                onSelect={onSelect}
                                isPopular={popularTemplateIds.includes(template.id)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                            <Search className="w-8 h-8 text-amber-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            No templates found
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Try adjusting your search or filters to find what you're looking for.
                        </p>
                        <button
                            onClick={() => {
                                setSearchQuery('');
                                setSelectedStyle('all');
                            }}
                            className="px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-semibold hover:from-amber-700 hover:to-orange-700 transition-colors"
                        >
                            Clear All Filters
                        </button>
                    </div>
                )}

                {/* Quick Tips */}
                {!searchQuery && selectedStyle === 'all' && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Award className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    Need help choosing?
                                </h4>
                                <ul className="space-y-1 text-sm text-gray-600">
                                    <li className="flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-amber-500" />
                                        <span><strong>Popular templates</strong> are marked with a badge - great for first-timers!</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-orange-500" />
                                        <span><strong>All templates are fully customizable</strong> - you can change colors, images, and content.</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Grid className="w-4 h-4 text-amber-600" />
                                        <span><strong>More sections = more features</strong> - check the section count on each card.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateSelector;
